import {
  DICTIONARY_SYLLABLES,
  readSyllable,
  splitSyllables,
  stripToneMarks,
  writeSyllable,
  type Syllable,
} from "@-/com.npmjs.kensio__pinyinjs";

import onetime from "@-/com.npmjs.onetime";

const getPinyinWordRegex = onetime(() => /[\p{Script=Latin}\p{M}:0-5^]+/gu);
const getNumberedSegmentRegex = onetime(() => /[^0-5]*[0-5]|[^0-5]+$/gu);
const getAsciiCircumflexRegex = onetime(() => /e\^/giu);
const N_L_UE = /^([nl])ue(?=[0-5]?$)/iu;
const NUMBER_R = /([0-5])([rR])$/u;
const TRAILING_TONE = /[0-5]$/u;
const NEUTRAL_FIVE = /5$/u;
const TONE_DIGIT = /[0-5]/u;
const WHITESPACE_SPLIT_RE = /\s+/u;

function preserveCase(source: string, output: string): string {
  if (source === source.toLocaleUpperCase()) {
    return output.toLocaleUpperCase();
  }
  return source.charAt(0) === source.charAt(0).toLocaleUpperCase()
    ? output.charAt(0).toLocaleUpperCase() + output.slice(1)
    : output;
}

/** Accept the legacy ASCII spellings used by existing cards and dictionary sources. */
function normalizeSpelling(value: string): string {
  return value
    .replaceAll(getAsciiCircumflexRegex(), (spelling) => (spelling.charAt(0) === "E" ? "Ê" : "ê"))
    .replace(N_L_UE, "$1üe")
    .replace(NUMBER_R, "$2$1");
}

function readPinyinParts(segment: string): { syllable: Syllable; text: string }[] | undefined {
  const spellings = readSyllable(segment) ? [segment] : splitSyllables(segment);
  if (spellings === undefined) {
    return undefined;
  }
  const numbered = TONE_DIGIT.test(segment);
  const parts: { syllable: Syllable; text: string }[] = [];
  for (const text of spellings) {
    const syllable = readSyllable(text);
    if (
      syllable === undefined ||
      !DICTIONARY_SYLLABLES.has(writeSyllable({ ...syllable, erhua: false }, "none")) ||
      (numbered && syllable.tone === undefined)
    ) {
      return undefined;
    }
    parts.push({ syllable, text });
  }
  return parts;
}

function mapPinyinText(
  value: string,
  format: (syllable: Syllable) => string,
  preserveMarked = false,
): string {
  return value
    .trim()
    .normalize("NFC")
    .replaceAll(getPinyinWordRegex(), (word) => {
      // Tone digits are explicit boundaries. Unsupported dialect readings such as gam3
      // must not be guessed as an unmarked ga followed by m3.
      const segments = normalizeSpelling(word).match(getNumberedSegmentRegex()) ?? [word];
      return segments
        .map((segment) => {
          if (preserveMarked && !TONE_DIGIT.test(segment) && stripToneMarks(segment) !== segment) {
            return segment;
          }
          return (
            readPinyinParts(segment)
              ?.map(({ syllable, text }) => preserveCase(text, format(syllable)))
              .join("") ?? segment
          );
        })
        .join("");
    });
}

/** Canonical ASCII pinyin: v for ü and 0 for an unmarked or explicit neutral tone. */
export function toToneNumbersPinyin(
  pinyin: string,
  { neutralToneNumber = "0" }: { neutralToneNumber?: "0" | "none" } = {},
): string {
  return mapPinyinText(pinyin, (syllable) => {
    const tone = syllable.tone === 5 ? undefined : syllable.tone;
    const numbered = writeSyllable({ ...syllable, tone }, "numbers")
      .replaceAll("ü", "v")
      .replaceAll("ê", "e^");
    return tone === undefined && neutralToneNumber === "0" ? `${numbered}0` : numbered;
  });
}

/** Toneless key used by pinyin.toml; its n/l syllables spell ü rather than ASCII v. */
export function tonelessSyllableForPinyinAssetLookup(syllable: string): string {
  const normalized = normalizeSpelling(syllable.trim().normalize("NFC").toLocaleLowerCase());
  const parsed = readSyllable(normalized);
  return parsed === undefined
    ? normalized.replace(TRAILING_TONE, "")
    : writeSyllable(parsed, "none");
}

/** The audio filename convention: v for ü, digits 1–4, and no neutral-tone digit. */
export function toPinyinAssetKey(syllable: string): string {
  return toToneNumbersPinyin(syllable.toLocaleLowerCase(), { neutralToneNumber: "none" });
}

/** Comparison keeps the tone, with neutral spelled 0 and n/l ü written as ü. */
export function toneNumberSyllableForComparison(syllable: string): string {
  const normalized = normalizeSpelling(syllable.trim().normalize("NFC").toLocaleLowerCase());
  const parsed = readSyllable(normalized);
  if (parsed === undefined) {
    const tone = normalized.match(TRAILING_TONE)?.[0] ?? "0";
    return normalized.replace(TRAILING_TONE, "") + (tone === "5" ? "0" : tone);
  }
  return writeSyllable({ ...parsed, tone: parsed.tone ?? 5 }, "numbers").replace(NEUTRAL_FIVE, "0");
}

/** Split numbered or marked readings, including connected syllables and apostrophes. */
export function splitToneNumbersPinyinSyllables(pinyin: string): string[] {
  return pinyin
    .trim()
    .split(WHITESPACE_SPLIT_RE)
    .filter((part) => part.length > 0)
    .flatMap((word) => {
      const segments = normalizeSpelling(word).match(getNumberedSegmentRegex()) ?? [word];
      return segments.flatMap(
        (segment) => readPinyinParts(segment)?.map(({ text }) => text) ?? [segment],
      );
    });
}

/** Format written pinyin for display, preserving capitalization, punctuation and spacing. */
export function toDiacriticPinyin(pinyin: string): string {
  return mapPinyinText(pinyin, (syllable) => writeSyllable(syllable, "marks"), true);
}

export function toDiacriticPinyinSyllables(pinyin: string): string[] {
  return splitToneNumbersPinyinSyllables(pinyin).map((syllable) => toDiacriticPinyin(syllable));
}

export function toDiacriticPinyinList(pinyinList: string[]): string[] {
  return pinyinList.map((syllable) => toDiacriticPinyin(syllable));
}
